import React from 'react';
import { Tooltip, TooltipTrigger, TooltipContent } from '../Tooltip';
import { Icons } from '../Icons';
import { cn } from '../../lib/utils';
import { useIconPresentation } from '../../contextProviders/IconPresentationProvider';

// Modern, labeled tool button (icon stacked over a small caption) inspired by
// contemporary mobile DICOM viewers: thin icon, tiny label, transparent on the
// bar, subtle hover, and an accent-tinted active state.
const baseClasses =
  'group inline-flex select-none flex-col items-center justify-center gap-1 rounded-lg transition-colors duration-150';
const defaultClasses = 'text-foreground/70 hover:bg-foreground/10 hover:text-foreground';
const activeClasses = 'bg-primary/20 text-primary ring-1 ring-inset ring-primary/40';
const disabledClasses = 'text-foreground/30 cursor-not-allowed';

// Vivid, function-coded icon colors so the toolbar reads as a clean multicolor
// set rather than a wall of grey. Applied as an INLINE color (see below) — OHIF's
// Tailwind build only emits semantic palette classes (primary/foreground/…), not
// sky-400/violet-400/…, so class-based tints silently render as grey. Hex values
// always apply via `currentColor`. Cool hues = manipulation, warm = measure/ROI,
// pink = annotate, purple = reconstruction/3D, green = view transforms.
const TOOL_ICON_COLOR: Record<string, string> = {
  // Manipulation — cool spectrum
  Pan: '#38bdf8',
  Zoom: '#22d3ee',
  WindowLevel: '#60a5fa',
  StackScroll: '#2dd4bf',
  Cine: '#34d399',
  Magnify: '#4ade80',
  AdvancedMagnify: '#a3e635',
  WindowLevelRegion: '#7dd3fc',
  ImageSliceSync: '#5eead4',
  // Measurement / ROI — warm spectrum
  Length: '#fbbf24',
  Bidirectional: '#fb923c',
  Angle: '#f59e0b',
  CobbAngle: '#fdba74',
  EllipticalROI: '#facc15',
  RectangleROI: '#fde047',
  CircleROI: '#fcd34d',
  PlanarFreehandROI: '#f97316',
  SplineROI: '#eab308',
  LivewireContour: '#fbbf24',
  Probe: '#bef264',
  CalibrationLine: '#fca5a5',
  UltrasoundDirectionalTool: '#fdba74',
  MeasurementTools: '#fbbf24',
  // Annotation — pink
  ArrowAnnotate: '#f472b6',
  // Reconstruction / 3D — purple spectrum
  MPR: '#a78bfa',
  Volume3D: '#c084fc',
  Crosshairs: '#e879f9',
  ReferenceLines: '#a5b4fc',
  TrackballRotate: '#d8b4fe',
  MPRProjection: '#c4b5fd',
  // View transforms — fresh green/teal + pops for invert/reset
  'rotate-right': '#34d399',
  'rotate-left': '#2dd4bf',
  flipHorizontal: '#5eead4',
  flipVertical: '#6ee7b7',
  invert: '#f0abfc',
  Reset: '#fda4af',
  ViewTools: '#34d399',
  // AI
  AIAutoSegment: '#e879f9',
  TumorIn3D: '#c084fc',
  // Layout & utilities
  Layout: '#818cf8',
  Capture: '#22d3ee',
  MoreTools: '#67e8f9',
  TagBrowser: '#7dd3fc',
  ImageOverlayViewer: '#93c5fd',
  SegmentLabelTool: '#6ee7b7',
};

interface ToolButtonProps {
  id: string;
  icon?: string;
  label?: string;
  tooltip?: string;
  size?: 'default' | 'small' | 'tiny' | 'icon';
  isActive?: boolean;
  disabled?: boolean;
  disabledText?: string;
  commands?: Record<string, unknown>;
  onInteraction?: (details: { itemId: string; commands?: Record<string, unknown> }) => void;
  className?: string;
  children?: React.ReactNode;
}

export { TOOL_ICON_COLOR };

function ToolButton(props: ToolButtonProps) {
  const {
    id,
    icon = 'MissingIcon',
    label,
    tooltip,
    size = 'default',
    disabled = false,
    isActive = false,
    disabledText,
    commands,
    onInteraction,
    className,
    children,
  } = props;

  const { className: iconClassName } = useIconPresentation();

  // Full-size toolbar buttons (size 'default' or the icon-presentation 'icon')
  // show a caption; compact 'small'/'tiny' usages stay as plain icon buttons.
  const showLabel = !!label && size !== 'small' && size !== 'tiny';
  // Toggle-style buttons (sync, cine, reference lines, …) signal their ON state
  // via the legacy `!text-primary` class from getToggledClassName rather than
  // an isActive prop — treat that as active so they get the highlight pill.
  const toggledOn =
    typeof className === 'string' &&
    className.includes('!text-primary') &&
    !className.includes('!text-foreground');
  const effectiveActive = isActive || toggledOn;
  const stateClasses = disabled
    ? disabledClasses
    : effectiveActive
      ? activeClasses
      : defaultClasses;
  // Per-function icon tint applied inline (kept even when active; cleared only
  // when disabled so disabled tools stay dim grey).
  const iconColor = disabled ? undefined : TOOL_ICON_COLOR[id];

  const defaultTooltip = label;
  const disabledTooltip = disabled && disabledText ? disabledText : null;
  const hasSecondaryTooltip = tooltip || disabledTooltip;
  const showTooltip = hasSecondaryTooltip || defaultTooltip;

  return (
    <Tooltip>
      <TooltipTrigger
        asChild
        className={cn(disabled && 'cursor-not-allowed')}
      >
        {/* span wrapper so the tooltip still fires when the button is disabled */}
        <span
          data-cy={id}
          data-tool={id}
          data-active={effectiveActive}
        >
          <button
            type="button"
            name={id}
            aria-label={defaultTooltip}
            disabled={disabled}
            onClick={() => {
              if (!disabled) {
                onInteraction?.({ itemId: id, commands });
              }
            }}
            className={cn(
              baseClasses,
              // Icon-only on tablet / mobile; icon + caption from `lg` up so the
              // full tool set fits the narrow bar without a hidden side-scroll.
              showLabel
                ? 'h-10 w-10 lg:h-[46px] lg:w-auto lg:min-w-[3.25rem] lg:px-1.5 lg:pb-1 lg:pt-1.5'
                : 'h-10 w-10',
              stateClasses,
              className
            )}
          >
            {children || (
              <span
                className="contents"
                style={iconColor ? { color: iconColor } : undefined}
              >
                <Icons.ByName
                  name={icon}
                  className={
                    showLabel ? 'h-7 w-7 lg:h-[22px] lg:w-[22px]' : iconClassName || 'h-7 w-7'
                  }
                />
              </span>
            )}
            {showLabel && (
              <span className="hidden max-w-[4.25rem] truncate text-[10px] font-medium leading-none tracking-tight lg:block">
                {label}
              </span>
            )}
          </button>
        </span>
      </TooltipTrigger>
      <TooltipContent
        side="bottom"
        className="text-wrap w-auto max-w-sm whitespace-normal break-words"
      >
        {showTooltip && (
          <div className="space-y-1">
            {defaultTooltip && <div className="text-sm">{defaultTooltip}</div>}
            {disabledTooltip ? (
              <div className="text-muted-foreground text-xs">{disabledTooltip}</div>
            ) : (
              tooltip && <div className="text-muted-foreground text-xs">{tooltip}</div>
            )}
          </div>
        )}
      </TooltipContent>
    </Tooltip>
  );
}

export default ToolButton;
